/**
 * Rudrik Fraud Detection
 * =================================
 * Real-time TX-level fraud scoring UI. Two modes:
 *   • Pick a sample scenario (one-click)
 *   • Fill the form (TX + device + customer + network)
 *
 * Result card surfaces: score gauge, 12 rules triggered, device/behavioral/
 * network signal grid, risk flags, STR urgency, recommended next action.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Activity, Cpu, Globe, Users, Zap,
  Loader2, FileWarning, CheckCircle2, XCircle, Eye, RefreshCw, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import { getFingerprintId, awaitFingerprintId } from "../lib/fingerprint";

const API = `${window.location.origin}/api/fraud-detect`;

const RISK_COLOR = {
  LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626",
};
const ACTION_COLOR = {
  ALLOW: "#22c55e", MONITOR: "#f59e0b", HOLD: "#f97316",
  NOTIFY_OFFICER: "#f97316", BLOCK: "#dc2626",
};
const SEV_COLOR = {
  CRITICAL: "#dc2626", HIGH: "#f97316", MEDIUM: "#f59e0b", LOW: "#22c55e",
};

function FormField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <input type={type} value={value} placeholder={placeholder} data-testid={testId}
             onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
             style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }} />
    </label>
  );
}

function ToggleField({ label, value, onChange, testId }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 600 }}>{label}</span>
    </label>
  );
}

function Pill({ label, value, color }) {
  const c = color || (value ? "#dc2626" : "#22c55e");
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 9px", background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6 }}>
      <span style={{ color: "#cbd5e1", fontSize: 10, fontWeight: 600 }}>{label}</span>
      <span style={{ color: c, fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}>{
        typeof value === "boolean" ? (value ? "yes" : "no") : (value ?? "—")
      }</span>
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section data-testid={testId} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
        {Icon && <Icon size={12} style={{ color: "#2563eb" }} />} {title}
      </div>
      {children}
    </section>
  );
}

export default function FraudDetectPage() {
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [result, setResult] = useState(null);
  const [rules, setRules] = useState([]);
  const [showRules, setShowRules] = useState(false);

  // form state
  const [amount, setAmount] = useState(45000);
  const [hour, setHour] = useState(11);
  const [count24h, setCount24h] = useState(1);
  const [rapidInOut, setRapidInOut] = useState(false);
  const [structuring, setStructuring] = useState(false);
  const [benWatchlist, setBenWatchlist] = useState(false);
  const [benNew, setBenNew] = useState(false);
  const [roundFreq, setRoundFreq] = useState(0);
  const [vpn, setVpn] = useState(false);
  const [tor, setTor] = useState(false);
  const [newDevice, setNewDevice] = useState(false);
  const [geoMismatch, setGeoMismatch] = useState(false);
  const [country, setCountry] = useState("IN");
  const [linkedAccounts, setLinkedAccounts] = useState(0);
  const [income, setIncome] = useState(80000);
  const [dormant, setDormant] = useState(0);
  const [emailBreached, setEmailBreached] = useState(false);
  const [pep, setPep] = useState(false);
  const [linkedFraud, setLinkedFraud] = useState(0);
  const [moneyMule, setMoneyMule] = useState(false);
  const [ipAddr, setIpAddr] = useState("");
  const [custEmail, setCustEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/sample-scenarios`, { withCredentials: true })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {});
    axios.get(`${API}/rules`, { withCredentials: true })
      .then(({ data }) => setRules(data.rules || []))
      .catch(() => {});
  }, []);

  const applyScenario = (s) => {
    const t = s.payload.transaction || {};
    const d = s.payload.device || {};
    const c = s.payload.customer || {};
    const n = s.payload.network || {};
    setAmount(t.amount_inr || 0); setHour(t.hour ?? 12); setCount24h(t.count_24h || 0);
    setRapidInOut(!!t.rapid_in_out_24h); setStructuring(!!t.structuring_pattern);
    setBenWatchlist(!!t.beneficiary_watchlisted); setBenNew(!!t.beneficiary_new);
    setRoundFreq(t.round_amount_count_30d || 0);
    setVpn(!!d.vpn); setTor(!!d.tor); setNewDevice(!!d.new_device);
    setGeoMismatch(!!d.geo_mismatch); setCountry(d.country || "IN");
    setLinkedAccounts(d.linked_accounts || 0);
    setIncome(c.monthly_income_inr || 0); setDormant(c.dormant_days || 0);
    setEmailBreached(!!c.email_breached); setPep(!!c.pep);
    setLinkedFraud(n.linked_fraud_accounts || 0); setMoneyMule(!!n.money_mule_pattern);
    setResult(null);
    toast.success(`Loaded: ${s.label}`);
  };

  const run = async () => {
    setLoading(true); setResult(null);
    const fingerprintId = await awaitFingerprintId(800);
    try {
      const payload = {
        transaction: {
          amount_inr: Number(amount) || 0, hour: Number(hour) || 0, count_24h: Number(count24h) || 0,
          rapid_in_out_24h: rapidInOut, structuring_pattern: structuring,
          beneficiary_watchlisted: benWatchlist, beneficiary_new: benNew,
          is_round_amount: Number(amount) % 10000 === 0,
          round_amount_count_30d: Number(roundFreq) || 0,
        },
        device: {
          vpn, tor, new_device: newDevice, geo_mismatch: geoMismatch,
          country, linked_accounts: Number(linkedAccounts) || 0,
          fingerprint_id: fingerprintId,
        },
        customer: {
          monthly_income_inr: Number(income) || 0, dormant_days: Number(dormant) || 0,
          email_breached: emailBreached, pep,
          email: custEmail || undefined,
        },
        network: {
          linked_fraud_accounts: Number(linkedFraud) || 0, money_mule_pattern: moneyMule,
        },
        ip_address: ipAddr || undefined,
      };
      const { data } = await axios.post(`${API}/score`, payload, { withCredentials: true, timeout: 25000 });
      setResult(data.result);
      const r = data.result;
      if (r.fraud_risk === "CRITICAL") toast.error(`${r.action} — CRITICAL · score ${r.fraud_score}`);
      else if (r.fraud_risk === "HIGH") toast.warning(`${r.action} — HIGH risk · score ${r.fraud_score}`);
      else if (r.fraud_risk === "MEDIUM") toast.warning(`${r.action} — MEDIUM · score ${r.fraud_score}`);
      else toast.success(`${r.action} — LOW · score ${r.fraud_score}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fraud scoring failed");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setAmount(0); setHour(12); setCount24h(0); setRapidInOut(false); setStructuring(false);
    setBenWatchlist(false); setBenNew(false); setRoundFreq(0);
    setVpn(false); setTor(false); setNewDevice(false); setGeoMismatch(false);
    setCountry("IN"); setLinkedAccounts(0);
    setIncome(0); setDormant(0); setEmailBreached(false); setPep(false);
    setLinkedFraud(0); setMoneyMule(false);
    setResult(null);
  };

  const riskColor = RISK_COLOR[result?.fraud_risk] || "#94a3b8";
  const actionColor = ACTION_COLOR[result?.action] || "#94a3b8";

  return (
    <div data-testid="fraud-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-1">
        <ShieldAlert style={{ width: 26, height: 26, color: "#dc2626" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Fraud Detection</h1>
        <span data-testid="session-badge" style={{ background: "rgba(220,38,38,0.10)", color: "#fca5a5", border: "1px solid #dc262655", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Real-time transaction scoring across 12 PMLA rules + device + behavioral + network signals.
        Returns BLOCK / HOLD / MONITOR / ALLOW in &lt; 1s.
      </p>

      {/* Sample scenarios */}
      {scenarios.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center", marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FlaskConical size={11} /> Quick demos:
          </span>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => applyScenario(s)} data-testid={`sample-${s.id}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Rules library toggle */}
      {rules.length > 0 && (
        <div data-testid="rules-library" style={{ marginBottom: 16 }}>
          <button onClick={() => setShowRules((v) => !v)} data-testid="toggle-rules"
            style={{ background: "transparent", border: "1px dashed #1e2530", color: "#94a3b8",
                     borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600,
                     cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <FileWarning size={11} /> {showRules ? "Hide" : "View"} all {rules.length} rules (R01–R{String(rules.length).padStart(2, "0")})
          </button>
          {showRules && (
            <div style={{ marginTop: 10, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
                Rudrik Fraud Detection — Rule Catalog · PMLA 2002 · RBI Fraud-Risk Master Directions
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {rules.map((r) => {
                  const c = SEV_COLOR[r.severity] || "#94a3b8";
                  return (
                    <div key={r.id} data-testid={`rule-card-${r.id}`} style={{ background: "#080c12", border: `1px solid ${c}40`, borderRadius: 6, padding: "8px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: c, fontSize: 9, fontWeight: 800, background: `${c}15`, padding: "1px 6px", borderRadius: 3, letterSpacing: 0.3 }}>{r.id}</span>
                        <span style={{ color: "#f1f5f9", fontSize: 12, fontWeight: 700 }}>{r.name}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{r.severity}</span>
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 3 }}>{r.description}</div>
                      <code style={{ display: "block", marginTop: 4, color: "#60a5fa", fontSize: 10, fontFamily: "ui-monospace, monospace", background: "#0d1117", padding: "3px 6px", borderRadius: 3, border: "1px solid #1e2530" }}>{r.rule}</code>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Section title="Transaction" icon={Activity} testId="form-tx">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <FormField label="Amount (INR)" value={amount} onChange={setAmount} type="number" testId="f-amount" />
            <FormField label="Hour (0-23)" value={hour} onChange={setHour} type="number" testId="f-hour" />
            <FormField label="Count in 24h" value={count24h} onChange={setCount24h} type="number" testId="f-count" />
            <FormField label="Round-amount freq 30d" value={roundFreq} onChange={setRoundFreq} type="number" testId="f-roundfreq" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <ToggleField label="Structuring pattern" value={structuring} onChange={setStructuring} testId="f-structuring" />
            <ToggleField label="Rapid in/out 24h" value={rapidInOut} onChange={setRapidInOut} testId="f-rapidinout" />
            <ToggleField label="Beneficiary on watchlist" value={benWatchlist} onChange={setBenWatchlist} testId="f-benwatchlist" />
            <ToggleField label="Beneficiary new" value={benNew} onChange={setBenNew} testId="f-bennew" />
          </div>
        </Section>

        <Section title="Device + IP" icon={Cpu} testId="form-device">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <FormField label="IP country (ISO-2)" value={country} onChange={(v) => setCountry(v.toUpperCase().slice(0,2))} testId="f-country" />
            <FormField label="Device-linked accounts" value={linkedAccounts} onChange={setLinkedAccounts} type="number" testId="f-linked" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <ToggleField label="VPN" value={vpn} onChange={setVpn} testId="f-vpn" />
            <ToggleField label="Tor" value={tor} onChange={setTor} testId="f-tor" />
            <ToggleField label="New device" value={newDevice} onChange={setNewDevice} testId="f-newdev" />
            <ToggleField label="Geo mismatch" value={geoMismatch} onChange={setGeoMismatch} testId="f-geo" />
          </div>
        </Section>

        <Section title="Customer Profile" icon={Users} testId="form-customer">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <FormField label="Monthly income (INR)" value={income} onChange={setIncome} type="number" testId="f-income" />
            <FormField label="Dormant days" value={dormant} onChange={setDormant} type="number" testId="f-dormant" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <ToggleField label="Email in breach corpus" value={emailBreached} onChange={setEmailBreached} testId="f-breached" />
            <ToggleField label="PEP" value={pep} onChange={setPep} testId="f-pep" />
          </div>
        </Section>

        <Section title="Network Graph" icon={Globe} testId="form-network">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <FormField label="Linked fraud accounts" value={linkedFraud} onChange={setLinkedFraud} type="number" testId="f-linkedfraud" />
          </div>
          <ToggleField label="Money-mule pattern detected" value={moneyMule} onChange={setMoneyMule} testId="f-mule" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <FormField label="IP address (IPQS/AbuseIPDB)" value={ipAddr} onChange={setIpAddr} testId="f-ip" />
            <FormField label="Customer email (HIBP)" value={custEmail} onChange={setCustEmail} testId="f-email" />
          </div>
          <div style={{ fontSize: 9, color: "#64748b", marginTop: 6 }}>
            Fingerprint: <span style={{ color: "#a855f7", fontFamily: "monospace" }} data-testid="fp-preview">{getFingerprintId().slice(0, 18)}…</span>
          </div>
        </Section>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10, marginBottom: 16 }}>
        <button onClick={run} disabled={loading} data-testid="run-btn"
          style={{ flex: 1, background: loading ? "#1e2530" : "linear-gradient(135deg, #dc2626, #991b1b)",
                   border: "none", color: "white", borderRadius: 6, padding: "11px 18px",
                   fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                   display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {loading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
          {loading ? "Scoring…" : "Score this transaction"}
        </button>
        <button onClick={reset} data-testid="reset-btn"
          style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8",
                   borderRadius: 6, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                   display: "inline-flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={11} /> Reset
        </button>
      </div>

      {result && (
        <>
          {/* Hero verdict */}
          <div data-testid="fraud-result" style={{
            background: "#0d1117", border: `1px solid ${actionColor}66`, borderRadius: 12, padding: 22, marginBottom: 12,
            display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 16, alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Decision</div>
              <div data-testid="action" style={{ color: actionColor, fontSize: 22, fontWeight: 800, marginTop: 4 }}>{result.action}</div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{result.explanation}</div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>
                {result.ai_mode} · {result.latency_ms}ms · FP {result.false_positive_probability}% · Type {result.fraud_type}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Fraud Score</div>
              <div data-testid="score" style={{ color: riskColor, fontSize: 38, fontWeight: 800 }}>{result.fraud_score}</div>
              <div data-testid="risk" style={{ color: riskColor, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{result.fraud_risk}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>STR</div>
              <div style={{ color: result.str_required ? "#fca5a5" : "#22c55e", fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                {result.str_required ? "Required" : "Not required"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 2 }}>{result.str_urgency}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Rules Hit</div>
              <div style={{ color: "#f1f5f9", fontSize: 26, fontWeight: 800, marginTop: 2 }}>{result.rules_triggered?.length || 0}</div>
              <div style={{ color: "#64748b", fontSize: 10 }}>of 12 PMLA rules</div>
            </div>
          </div>

          {/* Rules */}
          {result.rules_triggered?.length > 0 && (
            <Section title="Rules Triggered" icon={FileWarning} testId="rules-section">
              <div style={{ display: "grid", gap: 6 }}>
                {result.rules_triggered.map((r, i) => {
                  const c = SEV_COLOR[r.severity] || "#94a3b8";
                  return (
                    <div key={r.rule_id} data-testid={`rule-${i}`} style={{ background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: c, fontSize: 10, fontWeight: 800, background: `${c}15`, padding: "1px 6px", borderRadius: 3 }}>{r.rule_id}</span>
                        <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{r.name}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{r.severity}</span>
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4, marginLeft: 4 }}>{r.detail}</div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Signal grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Section title="Device Signals" icon={Cpu} testId="device-signals">
              <div style={{ display: "grid", gap: 6 }}>
                <Pill label="IP score" value={result.device_signals?.ip_score} color="#60a5fa" />
                <Pill label="VPN" value={result.device_signals?.vpn_detected} />
                <Pill label="Tor" value={result.device_signals?.tor_detected} />
                <Pill label="Proxy" value={result.device_signals?.proxy_detected} />
                <Pill label="New device" value={result.device_signals?.new_device} />
                <Pill label="Linked accts" value={result.device_signals?.device_linked_accounts} color={result.device_signals?.device_linked_accounts >= 3 ? "#dc2626" : "#22c55e"} />
                <Pill label="Country risk" value={result.device_signals?.country_risk} color={RISK_COLOR[result.device_signals?.country_risk] || "#22c55e"} />
                <Pill label="FATF country" value={result.device_signals?.fatf_country} />
              </div>
            </Section>

            <Section title="Behavioral" icon={Activity} testId="behav-signals">
              <div style={{ display: "grid", gap: 6 }}>
                <Pill label="Amount anomaly" value={result.behavioral_signals?.amount_anomaly} />
                <Pill label="Velocity anomaly" value={result.behavioral_signals?.velocity_anomaly} />
                <Pill label="Time anomaly" value={result.behavioral_signals?.time_anomaly} />
                <Pill label="Beneficiary anomaly" value={result.behavioral_signals?.beneficiary_anomaly} />
                <Pill label="Location anomaly" value={result.behavioral_signals?.location_anomaly} />
                <Pill label="Dormant reactivation" value={result.behavioral_signals?.dormant_reactivation} />
              </div>
            </Section>

            <Section title="Network" icon={Globe} testId="network-signals">
              <div style={{ display: "grid", gap: 6 }}>
                <Pill label="Shared device" value={result.network_signals?.shared_device_risk} />
                <Pill label="Shared beneficiary" value={result.network_signals?.shared_beneficiary_risk} />
                <Pill label="Linked fraud accts" value={result.network_signals?.linked_fraud_accounts} color={result.network_signals?.linked_fraud_accounts > 0 ? "#dc2626" : "#22c55e"} />
                <Pill label="Money mule" value={result.network_signals?.money_mule_risk} />
              </div>
            </Section>
          </div>

          {/* Risk flags + next action */}
          {result.risk_flags?.length > 0 && (
            <Section title="Risk Flags" icon={AlertTriangle} testId="flags-section">
              <div style={{ display: "grid", gap: 6 }}>
                {result.risk_flags.map((f, i) => {
                  const c = SEV_COLOR[f.severity] || "#94a3b8";
                  return (
                    <div key={i} data-testid={`flag-${i}`} style={{ background: `${c}10`, border: `1px solid ${c}55`, borderRadius: 6, padding: "6px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                      <AlertTriangle size={11} style={{ color: c }} />
                      <span style={{ color: c, fontSize: 11, fontWeight: 700 }}>{f.flag}</span>
                      <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 6 }}>{f.evidence}</span>
                      <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{f.severity} · {f.action}</span>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          <Section title="Recommended Action" icon={Eye} testId="next-action">
            <div style={{ color: "#cbd5e1", fontSize: 13 }}>→ {result.recommended_next_action}</div>
          </Section>

          {result.external_enrichment && (
            <Section title="External Signal Enrichment" icon={Globe} testId="enrichment-section">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { key: "ipqs", label: "IPQualityScore", data: result.external_enrichment?.ipqs, configured: result.external_enrichment?.configured?.ipqs },
                  { key: "abuseipdb", label: "AbuseIPDB", data: result.external_enrichment?.abuseipdb, configured: result.external_enrichment?.configured?.abuseipdb },
                  { key: "hibp", label: "HaveIBeenPwned", data: result.external_enrichment?.hibp, configured: result.external_enrichment?.configured?.hibp },
                ].map((s) => {
                  const live = s.data !== null && s.data !== undefined;
                  let summary = null;
                  if (live) {
                    if (s.key === "ipqs") summary = `fraud ${s.data.fraud_score}/100 · ${s.data.is_vpn ? "VPN " : ""}${s.data.is_proxy ? "PROXY " : ""}${s.data.is_tor ? "TOR " : ""}· ${s.data.country_code || "?"}`;
                    else if (s.key === "abuseipdb") summary = `abuse ${s.data.abuse_confidence_score}/100 · ${s.data.total_reports} reports · ${s.data.usage_type || "?"}`;
                    else summary = `${s.data.length} breach(es)${s.data.length ? " · " + (s.data[0]?.title || s.data[0]?.name || "").slice(0, 22) : ""}`;
                  }
                  return (
                    <div key={s.key} data-testid={`enrichment-${s.key}`} style={{
                      background: "#080c12", border: `1px solid ${live ? "#22c55e44" : "#1e2530"}`,
                      borderRadius: 6, padding: 10,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <span style={{ color: "#f1f5f9", fontSize: 11, fontWeight: 700 }}>{s.label}</span>
                        <span style={{ marginLeft: "auto", color: live ? "#22c55e" : (s.configured ? "#f59e0b" : "#64748b"),
                                       fontSize: 8, fontWeight: 800, padding: "2px 6px",
                                       background: live ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.10)",
                                       border: `1px solid ${live ? "#22c55e55" : "#1e2530"}`, borderRadius: 4 }}>
                          {live ? "LIVE" : (s.configured ? "NO DATA" : "NOT CONFIGURED")}
                        </span>
                      </div>
                      <div style={{ color: live ? "#cbd5e1" : "#64748b", fontSize: 10, lineHeight: 1.5 }}>
                        {summary || (s.configured ? "Configured but no data returned. Engine falls back to heuristics." : `Add ${s.key.toUpperCase()}_API_KEY to backend/.env`)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
